import React, { useState } from 'react';
import I18n from '@/modules/i18n';
import useFlags from '@/hooks/useFlags';
import { useMutation } from '@apollo/client';
import { UPDATE_SLIDE } from 'components/admin/SlideShow/Utils/slideShowGraphQL';
import SlidePageNumbers from '@/components/admin/SlideShow/Builder/SlideEditor/SlidePageNumbers/SlidePageNumbers';
import TagManagerTable from '@/components/admin/TagManager/TagManagerTable';
import useModelCorrelations from '@/components/admin/TagManager/hooks/useModelCorrelations';
import useDeepCompareEffect from 'use-deep-compare-effect';
import SlideAdminNotesPanel from '@/components/admin/SlideAdminNotes/SlideAdminNotesPanel';
import { useSlideStatuses } from '@/components/admin/SlideAdminNotes/useSlideStatuses';
import { SPAThemeScope } from '@/components/admin/SysadminSPA/shared/SPAThemeScope';
import SlideBuilderDrawer from '../SlideBuilderDrawer';
import { slidePropTypes } from '../../../Utils';
import styles from './EditorTabs.module.scss';
import TagsForm from '../../../../TagsForm';
import SlideReferenceMaterialsDropdown from '../../../../SlideReferenceMaterialsDropdown';
import showToast from '../../../../../common/Toast';
import TeacherNotes from '../../../../LessonGuides/TeacherNotes/TeacherNotes';
import useSlideShowStore, { ISlide } from '../../../stores/useSlideShowStore';
import useSlideShowContext from '../../../stores/useSlideShowContext';
import SlideConditions from '../../../../LessonGuides/SlideConditions';
import { checkArrayEquality } from '../../../../../../modules/TCIUtils';
import { isEmpty } from './editorTabUtils';
import {
  useSlideReferenceMaterials,
} from '../../../../SlideReferenceMaterialsDropdown/SlideReferenceMaterialsDropdown.utils';

const { displaySlideGroupHeaders, sysadminCorrelationsEdit } = useFlags();

const useTagIds = (setTagsCount: (next: number) => void, slide: ISlide) => {
  const [buildingTowardsIds, setBuildingTowardsIds] = useState(
    slide.buildingTowardsModelTags.map(({ tagId }) => tagId),
  );
  const [tagIds, setTagIds] = useState(slide.modelTags.map(({ tagId }) => tagId));

  const { data: currentSlideReferenceMaterials } = useSlideReferenceMaterials(slide.id);

  useDeepCompareEffect(() => {
    const nextBuildingTowardsIds = slide.buildingTowardsModelTags.map(({ tagId }) => tagId);
    const nextTagIds = slide.modelTags.map(({ tagId }) => tagId).filter(id => !nextBuildingTowardsIds.includes(id));

    setTagsCount(nextBuildingTowardsIds.length + nextTagIds.length + currentSlideReferenceMaterials.length);
    setBuildingTowardsIds(nextBuildingTowardsIds);
    setTagIds(nextTagIds);
  }, [slide, currentSlideReferenceMaterials.length]);

  return { buildingTowardsIds, tagIds };
};

const EditorTabs = ({ slide }) => {
  // Editor tab counts
  const [tagsCount, setTagsCount] = useState(0);

  const stafferId = useSlideShowContext(state => state.stafferId);
  const adminNoteStatuses = useSlideShowContext(state => state.adminNoteStatuses);
  const isSysAdmin = useSlideShowContext(state => state.isSysAdmin);
  const isSlideConditionsDrawerOpen = useSlideShowStore(state => state.isSlideConditionsDrawerOpen);
  const setIsSlideConditionsDrawerOpen = useSlideShowStore(state => state.setIsSlideConditionsDrawerOpen);
  const [updateSlideMutation] = useMutation(UPDATE_SLIDE);
  const { buildingTowardsIds, tagIds } = useTagIds(setTagsCount, slide);
  const [prevSlideId, setPrevSlideId] = useState(slide.id);
  const [isTeacherNotesDrawerOpen, setIsTeacherNotesDrawerOpen] = useState(false);
  const updateSlide = useSlideShowContext(state => state.updateSlide);
  const teacherNotesCount = isEmpty(slide.notes) ? 0 : 1;
  const { data: currentSlideReferenceMaterials } = useSlideReferenceMaterials(slide.id);

  const { data: adminNotes } = useSlideStatuses(parseInt(slide.id, 10), undefined, isSysAdmin);
  const adminNotesCount = adminNotes?.length ?? 0;

  const onSubmit = ({ building_towards_ids, tag_ids }) => {
    if (prevSlideId !== slide.id) {
      setPrevSlideId(slide.id);
      return;
    }

    const isSameBuildingTags = checkArrayEquality(buildingTowardsIds.sort(), building_towards_ids.sort());
    const isSameTags = checkArrayEquality(tagIds.sort(), tag_ids.sort());

    if (isSameBuildingTags && isSameTags) return;

    const mutationParams = {
      variables: {
        buildingTowardsIds: building_towards_ids,
        id: slide.id,
        tagIds: tag_ids,
      },
    };

    updateSlideMutation(mutationParams)
      .then((res) => {
        updateSlide(slide.id, res.data.updateSlide.slide);

        showToast('Successfully saved tags', { position: 'top-right' });

        // Update tag count after each submit.
        setTagsCount(building_towards_ids.length + tag_ids.length + currentSlideReferenceMaterials.length);
      })
      .catch(() => {
        showToast('Error saving tags', { msgType: 'error', position: 'top-right' });
      });
  };

  // Eager because the count sits on the collapsed trigger; TagManagerTable shares the query key.
  const {
    categories: correlationCategories,
    correlations,
    isError: correlationsError,
    isLoading: correlationsLoading,
  } = useModelCorrelations({
    enabled: isSysAdmin && sysadminCorrelationsEdit,
    modelId: parseInt(slide.id, 10),
    modelType: 'Slide',
  });

  // Standards-only on the flag-on branch; reference materials aren't correlations. Stays on the
  // unfiltered total — the table's own filters must not move the drawer title.
  const displayedTagsCount = sysadminCorrelationsEdit ? correlations.length : tagsCount;

  const lockIcon = <i className="tw-pl-1 fa fa-lock" />;

  return (
    <div className={styles.container}>
      {isSysAdmin && (
        <>
          <SlideBuilderDrawer
            buttonText={<>{`Admin Notes (${adminNotesCount})`} {lockIcon}</>}
            heightClassName="tw-h-auto tw-max-h-[70vh]"
            title={`Admin Notes (${adminNotesCount})`}
          >
            {/* The SPA tokens are pinned light to match the builder, as on admin/UserSearch. The drawer
                library sets user-select:none on the whole drawer for mouse users and treats a press-and-move
                inside it as a drag to dismiss; select-text and data-vaul-no-drag give the notes back to the
                cursor. */}
            <SPAThemeScope className="spa-theme-light">
              <div className="spa-theme-light tw-font-tci tw-pb-4 tw-select-text" data-vaul-no-drag>
                <SlideAdminNotesPanel
                  slideId={parseInt(slide.id, 10)}
                  stafferId={stafferId}
                  statuses={adminNoteStatuses}
                />
              </div>
            </SPAThemeScope>
          </SlideBuilderDrawer>
          <SlideBuilderDrawer
            buttonText={<>{`Tags (${displayedTagsCount})`} {lockIcon}</>}
            heightClassName={sysadminCorrelationsEdit ? 'tw-h-auto tw-max-h-[70vh]' : undefined}
            title={`Tags (${displayedTagsCount})`}
          >
            {sysadminCorrelationsEdit ? (
              <TagManagerTable
                categories={correlationCategories}
                correlations={correlations}
                error={correlationsError}
                isLoading={correlationsLoading}
                modelId={parseInt(slide.id, 10)}
                modelType="Slide"
                variant="panel"
              />
            ) : (
              <TagsForm
                key={slide.id}
                buildingTowardsIds={buildingTowardsIds}
                tagIds={tagIds}
                onSubmit={onSubmit}
              />
            )}
            <div className="tw-mt-4">
              <SlideReferenceMaterialsDropdown slideId={slide.id} />
            </div>
          </SlideBuilderDrawer>
          {displaySlideGroupHeaders && (
            <SlideBuilderDrawer
              buttonText={<>Page Numbers {lockIcon}</>}
              title="Page Numbers"
            >
              <SlidePageNumbers
                key={slide.id}
              />
            </SlideBuilderDrawer>
          )}
        </>
      )}
      <SlideBuilderDrawer
        buttonText={`Teacher Notes (${teacherNotesCount})`}
        isOpen={isTeacherNotesDrawerOpen}
        setIsOpen={setIsTeacherNotesDrawerOpen}
        title={`Teacher Notes (${teacherNotesCount})`}
      >
        <TeacherNotes key={slide.id} slide={slide} />
      </SlideBuilderDrawer>
      <SlideBuilderDrawer
        buttonText={`${I18n.t('slide_conditions')} (${slide.condition ? 1 : 0})`}
        isOpen={isSlideConditionsDrawerOpen}
        setIsOpen={setIsSlideConditionsDrawerOpen}
        title={`${I18n.t('slide_conditions')} (${slide.condition ? 1 : 0})`}
      >
        <SlideConditions />
      </SlideBuilderDrawer>
    </div>
  );
};

EditorTabs.propTypes = {
  slide: slidePropTypes.isRequired,
};

export default EditorTabs;
